import React from "react";
import { useNavigate } from "react-router-dom";
import type { PatientActivity } from "../../services/dashboard.service";
import SectionCard from "./SectionCard";

interface Props { activity: PatientActivity; }

const UsersIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const HeartIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const ROWS = [
  { key: "new",      label: "New Patients", subtitle: "Today",     bg: "bg-blue-50",    iconColor: "text-blue-600",    Icon: UsersIcon },
  { key: "followup", label: "Follow-ups",   subtitle: "Completed", bg: "bg-green-50",   iconColor: "text-green-600",   Icon: CheckCircleIcon },
  { key: "active",   label: "Active Cases", subtitle: "Ongoing",   bg: "bg-fuchsia-50", iconColor: "text-fuchsia-500", Icon: HeartIcon },
] as const;

export const PatientActivityCard: React.FC<Props> = ({ activity }) => {
  const navigate = useNavigate();
  const values: Record<typeof ROWS[number]["key"], number> = {
    new: activity.new_today,
    followup: activity.follow_ups_completed,
    active: activity.active_cases,
  };

  return (
    <SectionCard
      title="Patient Activity"
      action={
        <button
          onClick={() => navigate("/patients")}
          className="text-xs font-medium text-slate-500 hover:text-slate-800 flex items-center gap-1"
        >
          View All <span aria-hidden>›</span>
        </button>
      }
    >
      <ul className="space-y-2">
        {ROWS.map((r) => (
          <li key={r.key} className={`flex items-center gap-3 rounded-xl px-3 py-2.5 ${r.bg}`}>
            <span className={`h-10 w-10 rounded-xl bg-white/70 flex items-center justify-center ${r.iconColor}`}>
              <r.Icon />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900">{r.label}</p>
              <p className="text-[11px] text-slate-500">{r.subtitle}</p>
            </div>
            <span className="text-lg font-bold text-slate-900">{values[r.key]}</span>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
};

export default PatientActivityCard;
